import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvailabilityDot, VerifiedBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ImpactStrip } from "@/components/ImpactStrip";

export default function Profile() {
  const { user_id } = useParams();
  const [profile, setProfile] = useState(null);
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", bio: "", location: "", availability: "available" });

  useEffect(() => {
    api.get(`/users/${user_id}`).then(({ data }) => {
      setProfile(data);
      setForm({ name: data.name, bio: data.bio || "", location: data.location || "", availability: data.availability });
    });
  }, [user_id]);

  const save = async () => {
    const { data } = await api.patch("/users/me", form);
    setProfile((p) => ({ ...p, ...data }));
    setUser(data);
    setEditing(false);
    toast.success("Profile updated");
  };

  if (!profile) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading…</div></div>;
  const isMe = user?.user_id === profile.user_id;
  const grouped = { know: [], teach: [], learning: [], need_help: [] };
  for (const s of profile.skills || []) grouped[s.kind]?.push(s);

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="border rounded-3xl bg-card p-6 flex items-start gap-5 flex-wrap">
          <Avatar className="h-24 w-24 border-2">
            <AvatarImage src={profile.picture} />
            <AvatarFallback className="text-2xl bg-[color:hsl(var(--teal))] text-white">{profile.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-[240px]">
            {editing ? (
              <div className="space-y-2">
                <Input value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} placeholder="Name" />
                <Input value={form.location} onChange={(e) => setForm({...form, location:e.target.value})} placeholder="Location" />
                <Input value={form.bio} onChange={(e) => setForm({...form, bio:e.target.value})} placeholder="Bio" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h1 className="font-display font-bold text-2xl">{profile.name}</h1>
                  <AvailabilityDot status={profile.availability} withLabel />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{profile.location || "—"}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <VerifiedBadge level={profile.verification_level} />
                </div>
                <p className="mt-3 text-sm">{profile.bio || "No bio yet."}</p>
                <div className="mt-3 flex gap-4 text-sm">
                  <span><b className="font-display">{profile.people_helped || 0}</b> <span className="text-muted-foreground">people helped</span></span>
                  <span><b className="font-display">{profile.resolutions_success || 0}</b> <span className="text-muted-foreground">resolutions</span></span>
                  {profile.kudos_count > 0 && (
                    <span data-testid="profile-kudos"><b className="font-display text-[color:hsl(var(--amber))]">🙌 {profile.kudos_count}</b> <span className="text-muted-foreground">kudos</span></span>
                  )}
                </div>
              </>
            )}
          </div>
          {isMe && (
            editing
              ? <div className="flex gap-2"><Button onClick={save} className="btn-teal" data-testid="save-profile">Save</Button><Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button></div>
              : <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile">Edit profile</Button>
          )}
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {[
            { key: "know", title: "I KNOW", color: "text-[color:hsl(var(--teal))]" },
            { key: "teach", title: "I CAN TEACH", color: "text-[color:hsl(var(--amber))]" },
            { key: "learning", title: "I'M LEARNING", color: "text-blue-600 dark:text-blue-400" },
            { key: "need_help", title: "I NEED HELP WITH", color: "text-red-600 dark:text-red-400" },
          ].map(({ key, title, color }) => (
            <div key={key} className="border rounded-2xl bg-card p-5" data-testid={`profile-section-${key}`}>
              <div className={`text-xs font-mono-label ${color}`}>{title}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {grouped[key].length === 0
                  ? <span className="text-sm text-muted-foreground">—</span>
                  : grouped[key].map((s) => (
                    <span key={s.skill_id} className="rounded-full border bg-secondary px-3 py-1 text-xs">{s.name}</span>
                  ))
                }
              </div>
            </div>
          ))}
        </div>

        {isMe && <ImpactStrip variant="profile" />}
      </div>
    </div>
  );
}
